import React, { useContext } from 'react';
import { BreakpointContext, LARGE } from '@core-ui/breakpoint-provider';
import { CSSObject, forBrands, Theme } from '@core-ui/react-stitch';
import { useLocalize } from '@core-ui/localization-provider';
// @ts-ignore
import { RailContext } from '@cat/rail';
import { SIZE_FACET_REGULAR_VARIANT } from '../../localization-tokens';
import SizeButton from '../SizeButton';
import { Size } from '../../../../../types';

type StyleProps = {
  style: {
    name: string,
    sizes: Size[],
  },
  styleKey: string,
  variant: string,
  onSizeClick: (size: { [property: string]: any }, style: { [property: string]: any }) => void,
  isBRRedesignEnabled: boolean,
};

const getStyleLabelStyleForAthleta = (theme: Theme): CSSObject => ({
  color: theme.color.g3,
  letterSpacing: '0.01rem',
});

const getStyleLabelStyleForBananaRepublic = (theme: Theme, isBRRedesignEnabled: boolean): CSSObject => ({
  color: theme.color.b1,
  textTransform: 'capitalize',
  ...(isBRRedesignEnabled && { fontSize: '0.875rem' }),
  ...(isBRRedesignEnabled && { textAlign:'center' }),
});

const getStyleLabelStyleForGap = (theme: Theme): CSSObject => ({
  color: theme.color.b1,
  textTransform: 'capitalize',
});

const getStyleLabelStyleForOldNavy = (theme: Theme): CSSObject => ({
  ...theme.brandFont,
  fontWeight: 'bold',
  color: theme.color.g2,
  backgroundColor: theme.color.gray05,
  fontSize: '0.875rem',
  textAlign: 'center',
  textTransform: 'uppercase',
  padding: '0.5em',
});

const horizontalLineTextAccent = (t: Theme): CSSObject => ({
  overflow: 'hidden',
  '&:before, &:after': {
    backgroundColor: t.color.b2,
    content: '""',
    display: 'inline-block',
    height: '1px',
    position: 'relative',
    verticalAlign: 'middle',
    width: '50%',
  },
  '&:before': {
    right: '0.5em',
    marginLeft: '-50%',
  },
  '&:after': {
    left: '0.5em',
    marginRight: '-50%',
  }
});

const getStyleLabelStyleForBrands = (theme: Theme, isBRRedesignEnabled: boolean) => forBrands(theme, {
  at: getStyleLabelStyleForAthleta,
  br: getStyleLabelStyleForBananaRepublic(theme, isBRRedesignEnabled),
  brfs: getStyleLabelStyleForBananaRepublic(theme, isBRRedesignEnabled),
  gap: getStyleLabelStyleForGap,
  gapfs: getStyleLabelStyleForGap,
  on: getStyleLabelStyleForOldNavy,
});

const styleLabelStyleBRRedesign = (theme: Theme, styleKey: number): CSSObject => ({
  ...(styleKey === 0 && { 
    fontWeight:'bold',
    textTransform:'uppercase'
  }),
  ...(styleKey !== 0 && horizontalLineTextAccent(theme)),
  color: theme.color.b2,
});

const styleLabelStyle = (theme: Theme, isBRRedesignEnabled: boolean, styleKey: number): CSSObject => ({
  marginBottom: '0.5rem',
  padding: '1em 0.5em 0 0.5em',
  // @ts-ignore
  ...getStyleLabelStyleForBrands(theme, isBRRedesignEnabled),
  ...(isBRRedesignEnabled && styleLabelStyleBRRedesign(theme, styleKey)),
});

const getStyleStyleForAthleta = (theme: Theme): CSSObject => ({
  // @ts-ignore
  borderBottom: `1px solid ${theme.color.g6}`,
});

const getStyleStyleForBananaRepublic = (theme: Theme, isBRRedesignEnabled: boolean): CSSObject => ({
  ...(!isBRRedesignEnabled && { borderBottom:`1px solid ${theme.color.g4}` }),
});

const getStyleStyleForGap = (theme: Theme): CSSObject => ({
  borderBottom: `1px solid ${theme.color.g4}`,
});

const getStyleStyleForOldNavy = (theme: Theme): CSSObject => ({
  borderBottom: `1px solid ${theme.color.g5}`,
});

const getStyleStyleForBrands = (theme: Theme, isBRRedesignEnabled: boolean)=> forBrands(theme, {
  at: getStyleStyleForAthleta,
  br: getStyleStyleForBananaRepublic(theme, isBRRedesignEnabled),
  brfs: getStyleStyleForBananaRepublic(theme, isBRRedesignEnabled),
  gap: getStyleStyleForGap,
  gapfs: getStyleStyleForGap,
  on: getStyleStyleForOldNavy,
});

const styleStyle = (theme: Theme, isBRRedesignEnabled: boolean): CSSObject => ({
  position: 'relative',
  '&:last-child': {
    borderBottomWidth: 0,
  },
  // @ts-ignore
  ...getStyleStyleForBrands(theme, isBRRedesignEnabled),
});

const sizeStyleBRRedesign = (): CSSObject => ({
  paddingLeft: 0,
  paddingRight: 0,
  flexBasis: '25%',
});

const sizeStyle = (isBRRedesignEnabled: boolean): CSSObject => ({
  boxSizing: 'border-box',
  listStyleType: 'none',
  padding: '0 0.8em 1em 0',
  display: 'inline-block',
  ...(isBRRedesignEnabled && sizeStyleBRRedesign()),
});

const Style = (props: StyleProps) => {
  const {
    style,
    styleKey,
    variant,
    onSizeClick,
    isBRRedesignEnabled,
  } = props;

  const { onFacetChange } = useContext(RailContext);
  const { greaterOrEqualTo } = useContext(BreakpointContext);
  const { localize } = useLocalize();
  const isLarge = greaterOrEqualTo(LARGE);

  const styleSizesStyleBRRedesign = (isBRRedesignEnabled: boolean, numberOfButtons: number): CSSObject => ({
    ...(isBRRedesignEnabled && {
      flexWrap: 'wrap',
      textAlign: 'center',
      justifyContent: numberOfButtons >= 4 ? 'left' : 'center',
    })
  });

  const styleSizesStyle = (isBRRedesignEnabled: boolean, numberOfButtons: number): CSSObject => ({
    display: isBRRedesignEnabled ? 'flex' : 'block',
    ...styleSizesStyleBRRedesign(isBRRedesignEnabled, numberOfButtons),
    padding: '0.5rem 0 0.5rem 0.5rem',
    position: 'relative',
    ...isLarge ? {
      flexWrap: 'wrap',
      overflowX: 'auto',
      '&::after': {
        background: 'none',
      },
    } : {},
  });


  const getSearchFacetOptionValue = (includeVariantName: boolean, size: any) => `${includeVariantName
    ? `${variant} `
    : ''}${size.sfcName}: ${size.name}`;

  const onClick = (size: any) => {
    const includeVariantName = size?.variantName !== 'OMNI' &&
      size?.variantName !== localize(SIZE_FACET_REGULAR_VARIANT);

    const selectedFacet = {
      facetName: 'size',
      dimId: size.dimId,
      facetOption: size,
      id: size.id,
      isSelected: !size.selected,
      searchFacetOptionId: size.searchFacetOptionId,
      searchFacetOptionValue: getSearchFacetOptionValue(includeVariantName, size),
      sfcId: size.sfcId,
    };

    onFacetChange && onFacetChange(selectedFacet);
    onSizeClick(size, style);
  };

  return (
    <div key={`style-${styleKey.toString()}`} css={theme => styleStyle(theme, isBRRedesignEnabled)}>
      <p css={theme => styleLabelStyle(theme, isBRRedesignEnabled, +styleKey)}>{style.name}</p>
      <ul css={() => styleSizesStyle(isBRRedesignEnabled, style.sizes.length)}>
        {style.sizes.map((size, sizeKey) => (
          <li
            key={`size-${styleKey.toString()}-${sizeKey.toString()}`}
            css={() => sizeStyle(isBRRedesignEnabled)}
          >
            <SizeButton
              applied={size.selected}
              description={size.description}
              isBRRedesignEnabled={isBRRedesignEnabled}
              name={size.name}
              onClick={() => onClick(size)}
              style={style.name}
              variant={variant}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Style;